import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface Customer {
  id: string;
  customer_email: string;
  shipping_phone?: string;
  shipping_address?: string;
  shipping_city?: string;
  shipping_district?: string;
  shipping_postal_code?: string;
  billing_name?: string;
  billing_address?: string;
  billing_city?: string;
  billing_district?: string;
  billing_postal_code?: string;
  billing_tax_office?: string;
  billing_tax_number?: string;
  billing_same_as_shipping?: boolean;
  profile_picture_url?: string;
  created_at: string;
  updated_at?: string;
  order_count?: number;
}

const CustomersPanel = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Customer | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const { data: profiles, error } = await supabase
        .from('customer_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Her müşterinin sipariş sayısını al
      const customersWithOrders: Customer[] = [];
      for (const profile of (profiles || [])) {
        const { count } = await supabase
          .from('orders')
          .select('*', { count: 'exact', head: true })
          .eq('customer_email', profile.customer_email);

        customersWithOrders.push({
          ...profile,
          order_count: count || 0,
        });
      }

      setCustomers(customersWithOrders);
    } catch (error) {
      console.error('Müşteriler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomer = async (customer: Customer) => {
    setDeleting(true);
    try {
      // 1. Sepet kayıtlarını sil
      await supabase
        .from('cart')
        .delete()
        .eq('customer_email', customer.customer_email);

      // 2. Bildirimleri sil
      await supabase
        .from('notifications')
        .delete()
        .eq('customer_email', customer.customer_email);

      // 3. Müşterinin siparişlerini al
      const { data: customerOrders } = await supabase
        .from('orders')
        .select('id')
        .eq('customer_email', customer.customer_email);

      const orderIds = customerOrders?.map(o => o.id) || [];

      // 4. Tasarım revizyonlarını sil (order_id üzerinden)
      if (orderIds.length > 0) {
        await supabase
          .from('design_revisions')
          .delete()
          .in('order_id', orderIds);
      }

      // 5. Değerlendirmeleri sil
      await supabase
        .from('reviews')
        .delete()
        .eq('customer_email', customer.customer_email);

      // 6. Anket yanıtlarını sil
      await supabase
        .from('survey_responses')
        .delete()
        .eq('customer_email', customer.customer_email);

      // 7. Teklifleri sil
      await supabase
        .from('quotes')
        .delete()
        .eq('customer_email', customer.customer_email);

      // 8. Chat mesajlarını sil
      await supabase
        .from('chat_messages')
        .delete()
        .eq('customer_email', customer.customer_email);

      // 9. Chat konuşmalarını sil
      await supabase
        .from('chat_conversations')
        .delete()
        .eq('customer_email', customer.customer_email);

      // 10. Siparişleri sil
      await supabase
        .from('orders')
        .delete()
        .eq('customer_email', customer.customer_email);

      // 11. Son olarak müşteri profilini sil
      const { error: profileError } = await supabase
        .from('customer_profiles')
        .delete()
        .eq('id', customer.id);

      if (profileError) throw profileError;

      setCustomers(prev => prev.filter(c => c.id !== customer.id));
      if (selectedCustomer?.id === customer.id) {
        setSelectedCustomer(null);
      }
      setDeleteConfirm(null);
      setDeleteSuccess(true);
      setTimeout(() => setDeleteSuccess(false), 3000);
    } catch (error) {
      console.error('Müşteri silinirken hata:', error);
      alert('Müşteri silinirken bir hata oluştu: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredCustomers = customers.filter(c => {
    const q = searchQuery.toLowerCase();
    return (
      c.customer_email.toLowerCase().includes(q) ||
      (c.shipping_phone && c.shipping_phone.toLowerCase().includes(q)) ||
      (c.shipping_city && c.shipping_city.toLowerCase().includes(q)) ||
      (c.billing_name && c.billing_name.toLowerCase().includes(q))
    );
  });

  return (
    <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
      {/* Müşteri Listesi */}
      <div className="w-full lg:w-96 bg-white border-b lg:border-b-0 lg:border-r border-gray-200 flex flex-col max-h-[40vh] lg:max-h-none">
        <div className="p-4 lg:p-5 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-gray-900 font-semibold text-base lg:text-lg">Müşteriler</h2>
            <span className="bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-full font-medium">
              {filteredCustomers.length} kayıt
            </span>
          </div>
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="E-posta, telefon, şehir ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-10 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line text-3xl text-blue-500 animate-spin"></i>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-8 lg:py-12">
              <i className="ri-user-line text-4xl lg:text-5xl text-gray-300 mb-3"></i>
              <p className="text-gray-500 text-sm">Müşteri bulunamadı</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredCustomers.map((customer) => (
                <div
                  key={customer.id}
                  className={`relative group ${
                    selectedCustomer?.id === customer.id
                      ? 'bg-blue-50'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <button
                    onClick={() => setSelectedCustomer(customer)}
                    className="w-full p-3 lg:p-4 text-left transition-all cursor-pointer pr-12"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {customer.profile_picture_url ? (
                          <img src={customer.profile_picture_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-white font-medium text-sm">
                            {customer.customer_email.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="text-gray-900 font-medium text-sm truncate">
                            {customer.billing_name || customer.customer_email.split('@')[0]}
                          </h3>
                          <span className="text-gray-400 text-xs flex-shrink-0 ml-2">{formatDate(customer.created_at)}</span>
                        </div>
                        <p className="text-gray-500 text-xs truncate mb-1.5">{customer.customer_email}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {customer.shipping_city && (
                            <span className="flex items-center gap-1 text-gray-400 text-xs">
                              <i className="ri-map-pin-line"></i>
                              {customer.shipping_city}
                            </span>
                          )}
                          {customer.shipping_phone && (
                            <span className="flex items-center gap-1 text-gray-400 text-xs">
                              <i className="ri-phone-line"></i>
                              {customer.shipping_phone}
                            </span>
                          )}
                          <span className="flex items-center gap-1 bg-blue-50 text-blue-600 text-xs px-1.5 py-0.5 rounded">
                            <i className="ri-shopping-bag-line"></i>
                            {customer.order_count} sipariş
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm(customer);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer opacity-0 group-hover:opacity-100"
                    title="Müşteriyi Sil"
                  >
                    <i className="ri-delete-bin-line text-lg"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Müşteri Detayı */}
      <div className="flex-1 bg-gray-50 flex flex-col overflow-hidden">
        {selectedCustomer ? (
          <>
            <div className="p-4 lg:p-6 border-b border-gray-200 bg-white">
              <div className="flex items-center gap-3 lg:gap-4">
                <div className="w-12 h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center shadow-sm flex-shrink-0 overflow-hidden">
                  {selectedCustomer.profile_picture_url ? (
                    <img src={selectedCustomer.profile_picture_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white font-semibold text-lg lg:text-xl">
                      {selectedCustomer.customer_email.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-gray-900 font-semibold text-lg lg:text-xl truncate">
                    {selectedCustomer.billing_name || selectedCustomer.customer_email.split('@')[0]}
                  </h2>
                  <p className="text-gray-500 text-xs lg:text-sm truncate">{selectedCustomer.customer_email}</p>
                </div>
                <button
                  onClick={() => setDeleteConfirm(selectedCustomer)}
                  className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line"></i>
                  Müşteriyi Sil
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 lg:p-6">
              <div className="max-w-3xl space-y-4 lg:space-y-6">
                {/* Özet Kartları */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-shopping-bag-line text-xl text-blue-500"></i>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Sipariş</p>
                        <p className="text-gray-900 font-bold text-lg">{selectedCustomer.order_count}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-line text-xl text-green-500"></i>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Kayıt Tarihi</p>
                        <p className="text-gray-900 font-semibold text-sm">{formatDate(selectedCustomer.created_at)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-xl text-amber-500"></i>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Son Güncelleme</p>
                        <p className="text-gray-900 font-semibold text-sm">
                          {selectedCustomer.updated_at ? formatDate(selectedCustomer.updated_at) : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* İletişim Bilgileri */}
                <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200 shadow-sm">
                  <h3 className="text-gray-900 font-medium mb-3 lg:mb-4 flex items-center gap-2 text-sm lg:text-base">
                    <i className="ri-contacts-line text-blue-600"></i>
                    İletişim Bilgileri
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200 flex-shrink-0">
                        <i className="ri-mail-line text-xl text-gray-600"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-500 text-xs">E-posta</p>
                        <p className="text-gray-900 text-sm truncate">{selectedCustomer.customer_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200 flex-shrink-0">
                        <i className="ri-phone-line text-xl text-gray-600"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-500 text-xs">Telefon</p>
                        <p className="text-gray-900 text-sm">{selectedCustomer.shipping_phone || 'Belirtilmemiş'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Kargo Adresi */}
                <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200 shadow-sm">
                  <h3 className="text-gray-900 font-medium mb-3 lg:mb-4 flex items-center gap-2 text-sm lg:text-base">
                    <i className="ri-truck-line text-green-600"></i>
                    Kargo Adresi
                  </h3>
                  {selectedCustomer.shipping_address ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="sm:col-span-2">
                          <p className="text-gray-500 text-xs">Adres</p>
                          <p className="text-gray-900 text-sm">{selectedCustomer.shipping_address}</p>
                        </div>
                        {(selectedCustomer.shipping_city || selectedCustomer.shipping_district) && (
                          <div>
                            <p className="text-gray-500 text-xs">İl / İlçe</p>
                            <p className="text-gray-900 text-sm">
                              {[selectedCustomer.shipping_city, selectedCustomer.shipping_district].filter(Boolean).join(' / ')}
                            </p>
                          </div>
                        )}
                        {selectedCustomer.shipping_postal_code && (
                          <div>
                            <p className="text-gray-500 text-xs">Posta Kodu</p>
                            <p className="text-gray-900 text-sm">{selectedCustomer.shipping_postal_code}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                      <i className="ri-map-pin-line text-2xl text-gray-300 mb-2"></i>
                      <p className="text-gray-500 text-sm">Kargo adresi belirtilmemiş</p>
                    </div>
                  )}
                </div>

                {/* Fatura Bilgileri */}
                <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200 shadow-sm">
                  <h3 className="text-gray-900 font-medium mb-3 lg:mb-4 flex items-center gap-2 text-sm lg:text-base">
                    <i className="ri-file-list-3-line text-indigo-600"></i>
                    Fatura Bilgileri
                  </h3>
                  {selectedCustomer.billing_same_as_shipping === false && selectedCustomer.billing_address ? (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {selectedCustomer.billing_name && (
                          <div className="sm:col-span-2">
                            <p className="text-gray-500 text-xs">Ad / Ünvan</p>
                            <p className="text-gray-900 text-sm font-medium">{selectedCustomer.billing_name}</p>
                          </div>
                        )}
                        {selectedCustomer.billing_address && (
                          <div className="sm:col-span-2">
                            <p className="text-gray-500 text-xs">Adres</p>
                            <p className="text-gray-900 text-sm">{selectedCustomer.billing_address}</p>
                          </div>
                        )}
                        {(selectedCustomer.billing_city || selectedCustomer.billing_district) && (
                          <div>
                            <p className="text-gray-500 text-xs">İl / İlçe</p>
                            <p className="text-gray-900 text-sm">
                              {[selectedCustomer.billing_city, selectedCustomer.billing_district].filter(Boolean).join(' / ')}
                            </p>
                          </div>
                        )}
                        {selectedCustomer.billing_postal_code && (
                          <div>
                            <p className="text-gray-500 text-xs">Posta Kodu</p>
                            <p className="text-gray-900 text-sm">{selectedCustomer.billing_postal_code}</p>
                          </div>
                        )}
                        {selectedCustomer.billing_tax_office && (
                          <div>
                            <p className="text-gray-500 text-xs">Vergi Dairesi</p>
                            <p className="text-gray-900 text-sm">{selectedCustomer.billing_tax_office}</p>
                          </div>
                        )}
                        {selectedCustomer.billing_tax_number && (
                          <div>
                            <p className="text-gray-500 text-xs">Vergi No</p>
                            <p className="text-gray-900 text-sm font-mono">{selectedCustomer.billing_tax_number}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : selectedCustomer.billing_same_as_shipping === true ? (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                      <p className="text-indigo-700 text-sm flex items-center gap-2">
                        <i className="ri-checkbox-circle-line"></i>
                        Fatura adresi kargo adresiyle aynı
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                      <i className="ri-file-list-3-line text-2xl text-gray-300 mb-2"></i>
                      <p className="text-gray-500 text-sm">Fatura bilgisi belirtilmemiş</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center">
              <i className="ri-user-line text-5xl lg:text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-sm lg:text-base">Detayları görmek için bir müşteri seçin</p>
            </div>
          </div>
        )}
      </div>

      {/* Silme Onay Popup */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-user-unfollow-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-gray-900 font-semibold text-lg text-center mb-2">
              Müşteriyi Silmek İstediğinizden Emin Misiniz?
            </h3>
            <p className="text-gray-600 text-sm text-center mb-2">
              <span className="font-medium text-gray-900">{deleteConfirm.customer_email}</span>
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
              <p className="text-red-700 text-xs flex items-start gap-2">
                <i className="ri-error-warning-line text-base flex-shrink-0 mt-0.5"></i>
                <span>Bu müşteriye ait tüm siparişler, mesajlar, bildirimler, sepet ve değerlendirmeler kalıcı olarak silinecektir. Bu işlem geri alınamaz.</span>
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={() => handleDeleteCustomer(deleteConfirm)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {deleting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Siliniyor...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line"></i>
                    Sil
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Başarı Bildirimi */}
      {deleteSuccess && (
        <div className="fixed top-6 right-6 z-50 bg-green-500 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 animate-[slideIn_0.3s_ease-out]">
          <i className="ri-checkbox-circle-fill text-xl"></i>
          <span className="text-sm font-medium">Müşteri başarıyla silindi</span>
        </div>
      )}
    </div>
  );
};

export default CustomersPanel;
